import React from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  Modal,
} from 'react-native';
import Constants from 'expo-constants';
import ENV from 'app/utils/ENV';
import Text from 'app/components/Text';
import DatePicker from 'app/components/DatePicker';
import narrativeActions from 'app/utils/narrativeActions';
import valueActions from 'app/utils/valueActions';
import { StyleContext } from 'app/contexts/Style';
import { Feather } from '@expo/vector-icons';

function formatDateParam(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// "2025-05-25" → local midnight (avoids UTC-offset day shift)
function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default class NarrativeModal extends React.Component {
  static contextType = StyleContext;

  state = {
    startDate: parseLocalDate(this.props.chapter?.start_at) ?? (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d; })(),
    endDate: parseLocalDate(this.props.chapter?.end_at) ?? new Date(),
    loading: false,
    narrative: null,
    narrativeTitle: null,
    error: null,
    saving: false,
    saved: false,
    useClaude: false,
  };

  componentDidMount() {
    setTimeout(() => this.generateRef?.focus(), 150);
  }

  generate = async () => {
    const { startDate, endDate, useClaude } = this.state;
    this.setState({ loading: true, narrative: null, error: null, saved: false });
    const res = await narrativeActions.generate(
      formatDateParam(startDate),
      formatDateParam(endDate),
      useClaude,
    );
    if (res?.success) {
      this.setState({ loading: false, narrative: res.narrative, narrativeTitle: res.title });
    } else {
      this.setState({ loading: false, error: res?.error || 'Something went wrong.' });
    }
  };

  buildDateSuffix = (startDate, endDate, chapter) => {
    const range = chapter?.range ?? 1;
    const year = startDate.getFullYear();

    if (range >= 300) {
      // Year
      return `${year}`;
    } else if (range >= 60) {
      // Season
      const month = startDate.getMonth() + 1;
      const season = month >= 3 && month <= 5 ? 'Spring'
        : month >= 6 && month <= 8 ? 'Summer'
        : month >= 9 && month <= 11 ? 'Autumn'
        : 'Winter';
      return `${season} ${year}`;
    } else if (range >= 20) {
      // Month
      const monthNames = ['January','February','March','April','May','June',
                          'July','August','September','October','November','December'];
      return `${monthNames[startDate.getMonth()]} ${year}`;
    } else if (range >= 5) {
      // Week — use chapter title + year
      const label = chapter?.title;
      return label ? `${label} ${year}` : `${year}`;
    } else {
      // Day — use chapter title or day-of-week + m/d
      const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const label = chapter?.title || dayNames[startDate.getDay()];
      return `${label} ${startDate.getMonth() + 1}/${startDate.getDate()}`;
    }
  };

  buildTitle = (base, startDate, endDate, chapter) => {
    const suffix = this.buildDateSuffix(startDate, endDate, chapter);
    return `${base} – ${suffix}`;
  };

  saveAsNote = async () => {
    const { narrative, narrativeTitle, startDate, endDate } = this.state;
    const { chapter } = this.props;
    if (!narrative) return;
    this.setState({ saving: true });
    const dateSuffix = this.buildDateSuffix(startDate, endDate, chapter);
    const baseTitle = narrativeTitle || dateSuffix;
    const title = this.buildTitle(baseTitle, startDate, endDate, chapter);
    await valueActions.create({ title, text: narrative, is_note: true, category: 'narration', parent_ids: [10558], completed_at: new Date().toISOString() }, chapter?.id ?? null);
    this.setState({ saving: false, saved: true });
    this.props.onSaved?.();
  };

  render() {
    const { onClose } = this.props;
    const { startDate, endDate, loading, narrative, narrativeTitle, error, saving, saved, useClaude } = this.state;
    const { colors, sizes } = this.context;

    const panel = (
      <View style={ENV.WEB ? {
        width: 460,
        height: '100%',
        backgroundColor: colors.empty,
        borderLeftWidth: 1,
        borderLeftColor: colors.trim,
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 16,
      } : {
        flex: 1,
        backgroundColor: colors.empty,
        paddingTop: Constants.statusBarHeight,
      }}>
        {/* Header */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: 20,
          paddingVertical: 14,
          borderBottomWidth: 1,
          borderBottomColor: colors.trim,
        }}>
          <Text bold style={{ fontSize: sizes.large }}>Generate Narrative</Text>
          <TouchableOpacity onPress={onClose}>
            <Feather name="x" size={sizes.large} color={colors.foreground} />
          </TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
          {/* Date range */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 16,
            zIndex: 10,
          }}>
            <Text style={{ color: colors.lightForeground, marginRight: 8 }}>From</Text>
            <DatePicker
              dateOnly
              value={startDate}
              selected={startDate}
              onChange={(d) => this.setState({ startDate: d, narrative: null, saved: false })}
            />
            <Text style={{ color: colors.lightForeground, marginHorizontal: 8 }}>to</Text>
            <DatePicker
              dateOnly
              value={endDate}
              selected={endDate}
              onChange={(d) => this.setState({ endDate: d, narrative: null, saved: false })}
            />
          </View>

          {/* Generate button + Claude toggle */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <TouchableOpacity
              ref={(r) => (this.generateRef = r)}
              style={{
                backgroundColor: colors.trim,
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 8,
                alignItems: 'center',
              }}
              onPress={this.generate}
              disabled={loading}
            >
              {loading
                ? <ActivityIndicator color="#fff" />
                : <Text bold style={{ color: '#fff' }}>Generate</Text>
              }
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => this.setState({ useClaude: !useClaude })}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <View style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                backgroundColor: useClaude ? colors.trim : colors.lightForeground,
                justifyContent: 'center',
                paddingHorizontal: 2,
              }}>
                <View style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  backgroundColor: '#fff',
                  alignSelf: useClaude ? 'flex-end' : 'flex-start',
                }} />
              </View>
              <Text style={{ color: colors.foreground }}>Claude</Text>
            </TouchableOpacity>
          </View>

          {/* Error */}
          {!!error && (
            <Text style={{ color: 'red', marginTop: 12 }}>{error}</Text>
          )}

          {/* Narrative output */}
          {!!narrative && (
            <View style={{ marginTop: 20 }}>
              {!!narrativeTitle && (
                <Text bold style={{ fontSize: sizes.large, marginBottom: 10, color: colors.foreground }}>
                  {this.buildTitle(narrativeTitle, startDate, endDate, this.props.chapter)}
                </Text>
              )}
              <Text style={{ lineHeight: sizes.default * 1.7, color: colors.foreground }}>
                {narrative}
              </Text>

              <TouchableOpacity
                style={{
                  backgroundColor: colors.trim,
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 8,
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  marginTop: 16,
                  opacity: saved ? 0.5 : 1,
                }}
                onPress={this.saveAsNote}
                disabled={saving || saved}
              >
                {saving
                  ? <ActivityIndicator color="#fff" />
                  : <Text bold style={{ color: '#fff' }}>
                      {saved ? 'Saved!' : 'Save as Note'}
                    </Text>
                }
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>
    );

    // Native: fullscreen modal is the right affordance on a phone.
    if (!ENV.WEB) {
      return (
        <Modal visible transparent animationType="fade" onRequestClose={onClose}>
          <View style={{ flex: 1 }}>{panel}</View>
        </Modal>
      );
    }

    // Web: a right-docked panel with no backdrop. This component is mounted at
    // the Router root, so position:absolute pins it to the viewport while the
    // rest of the app stays scrollable and interactive (no Modal portal that
    // would capture every pointer event).
    return (
      <View
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
        }}
      >
        {panel}
      </View>
    );
  }

}
